import React, { useEffect, useRef, useState } from 'react';
import CatalogImage from '../silvr/CatalogImage';
import { isConfirmedMatch } from '../silvr/MatchBadge';
import { hasPrice } from '../silvr/price';
import { ChevronLeftIcon, XIcon } from '../silvr/icons';
import type { ShoppableProduct, StoreItem } from '../silvr/types';
import SilvrMark from './SilvrMark';

interface ShopPanelProps {
  open: boolean;
  onClose: () => void;
  /** The garments in the paused frame, one card each. */
  sceneItems: ShoppableProduct[];
  /** Every garment published for the clip ("View all shoppable items in this episode"). */
  episodeItems: ShoppableProduct[];
  /** "Show · S2 E7 · 0:08": where the scene is. */
  contextLabel: string;
  /** What the sheet is about right now, so the player frames the right circle (and draws the line). */
  onFocusChange: (focus: SheetFocus) => void;
  /** The scene card joined to its circle by the line, drawn with a stronger frame so it reads as the one selected. */
  pointedId: string | null;
}

/** What the sheet points the picture at (Jason, 2026-10-29):
 *  - 'scene': the scene list, with the card under the pointer (null: the first one); the
 *    player frames that card's circle and draws a line to it;
 *  - 'open': an item open in the sheet, in its Details or Similar matches, or the garment a similar
 *    match is similar to; its circle stays framed, with no line (there is no card to draw from);
 *  - 'none': "Shop this episode", which frames nothing. */
export type SheetFocus = { kind: 'scene'; hoverId: string | null } | { kind: 'open'; item: ShoppableProduct } | { kind: 'none' };

type Scope = 'scene' | 'episode';
type View =
  | { kind: 'list'; scope: Scope }
  | { kind: 'item'; item: ShoppableProduct; tab: 'details' | 'similar'; from: Scope }
  // A similar match opened from "Similar matches": its own details, back to the list it came from.
  | { kind: 'match'; match: StoreItem; parent: ShoppableProduct; from: Scope };

const priceLabel = (item: StoreItem) => (hasPrice(item.price) ? item.price : 'Check retailer');

/** Where "Shop now" goes: the first listing, through the affiliate redirect (toStoreItem). */
const shopUrl = (item: StoreItem) => item.links?.[0]?.url ?? item.url;

/** "3 listings available" under the brand (Josh, 2026-10-27: several Poshmark listings looked the same
 *  but for their price; the line says there is a choice). Only with 2 or more listings (Jason,
 *  2026-10-29): with one, the item shows its price and "Shop now", and there is no choice to announce. */
const hasChoiceOfListings = (item: StoreItem) => (item.links?.length ?? 0) >= 2;
const listingsLabel = (item: StoreItem) => (hasChoiceOfListings(item) ? `${item.links!.length} listings available` : null);

const ExternalIcon: React.FC = () => (
  <svg width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path d="M4 2h6v6M10 2 2.5 9.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SimilarTag: React.FC<{ item: StoreItem }> = ({ item }) => (!isConfirmedMatch(item.matchType) ? <span className="tv-card__similar-tag">Similar style</span> : null);

/** A garment as a tall photo card. The photo and "Shop now" both open its details first (2026-10-27
 *  review, and Jason, 2026-10-29): the shopper sees the price and the listings before leaving; only
 *  "Shop now" inside Details and the listing rows go out to the store. "See similar" opens the similar
 *  matches. Every card is the same: no "Featured" chip (Paula, 2026-10-29). */
const ItemCard: React.FC<{
  item: ShoppableProduct;
  onOpen: () => void;
  onSimilar: () => void;
  onHover: (id: string | null) => void;
  pointed: boolean;
}> = ({ item, onOpen, onSimilar, onHover, pointed }) => (
  <article
    className={`tv-card ${pointed ? 'is-pointed' : ''}`}
    data-item-id={item.id}
    onMouseEnter={() => onHover(item.id)}
    onMouseLeave={() => onHover(null)}
    // Moving focus between the card's own buttons stays on the card: no new line for that.
    onFocus={(event) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) onHover(item.id);
    }}
    onBlur={(event) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) onHover(null);
    }}
  >
    <button type="button" className="tv-card__link" onClick={onOpen} aria-label={`Details of ${item.name} by ${item.brand}`} />
    <div className="tv-card__media">
      <CatalogImage src={item.image} alt="" className="tv-card__img" />
    </div>
    <div className="tv-card__body">
      <div className="tv-card__text">
        <p className="tv-card__brand">
          {item.brand}
          <SimilarTag item={item} />
        </p>
        <p className="tv-card__name">{item.name}</p>
        <p className="tv-card__price">{priceLabel(item)}</p>
        {listingsLabel(item) && <p className="tv-card__listings">{listingsLabel(item)}</p>}
      </div>
      <div className="tv-card__actions">
        <button type="button" className="tv-btn tv-btn--gold" onClick={onOpen}>
          Shop now
        </button>
        {item.similar.length > 0 && (
          <button type="button" className="tv-card__see-similar" onClick={onSimilar}>
            See similar
          </button>
        )}
      </div>
    </div>
  </article>
);

/** A row in "Similar matches": thumbnail, brand, name, price. It opens the match's own details, the same
 *  view as a garment's (2026-10-27 review), and from there "Shop now" goes to the store. */
const MatchRow: React.FC<{ item: StoreItem; onOpen: () => void }> = ({ item, onOpen }) => (
  <li className="tv-match">
    <button type="button" className="tv-match__link" onClick={onOpen} aria-label={`Details of ${item.name} by ${item.brand}`} />
    <CatalogImage src={item.image} alt="" className="tv-match__img" />
    <div className="tv-match__text">
      <p className="tv-match__brand">{item.brand}</p>
      <p className="tv-match__name">{item.name}</p>
      <p className="tv-match__price">{priceLabel(item)}</p>
      {listingsLabel(item) && <p className="tv-match__listings">{listingsLabel(item)}</p>}
    </div>
    <span className="tv-match__chevron" aria-hidden="true">
      ›
    </span>
  </li>
);

/** A product's details: the photo, brand, name, price, its listings when there is a choice of two or
 *  more, and "Shop now" out to the store (the first listing). */
const Details: React.FC<{ item: StoreItem }> = ({ item }) => (
  <div className="tv-details">
    <div className="tv-details__media">
      <CatalogImage src={item.image} alt={item.name} className="tv-details__img" />
    </div>
    <p className="tv-card__brand">
      {item.brand}
      <SimilarTag item={item} />
    </p>
    <h3 className="tv-details__name">{item.name}</h3>
    <p className="tv-details__price">{priceLabel(item)}</p>
    {hasChoiceOfListings(item) && (
      <>
        <p className="tv-details__listings">{listingsLabel(item)}</p>
        <ul className="tv-listings" aria-label="Where it is on sale">
          {item.links!.map((link) => (
            <li key={link.url}>
              <a className="tv-listing" href={link.url} target="_blank" rel="noopener noreferrer">
                <span>{link.marketplace}</span>
                <span className="tv-listing__meta">{[link.size ? `Size ${link.size}` : null, link.price].filter(Boolean).join(' · ')}</span>
                <ExternalIcon />
              </a>
            </li>
          ))}
        </ul>
      </>
    )}
    <a className="tv-btn tv-btn--gold tv-btn--block" href={shopUrl(item)} target="_blank" rel="noopener noreferrer">
      Shop now <ExternalIcon />
    </a>
  </div>
);

/**
 * The dark side sheet of the "Shop by TV" design (Paula's mockups, 2026-10-24; reviewed 2026-10-27):
 * the garments in the paused frame, each as its best match. A card opens its details, "See similar" its
 * similar matches, and a match its own details; only "Shop now" and a listing leave for the store.
 */
const ShopPanel: React.FC<ShopPanelProps> = ({ open, onClose, sceneItems, episodeItems, contextLabel, onFocusChange, pointedId }) => {
  const [view, setView] = useState<View>({ kind: 'list', scope: 'scene' });
  const panelRef = useRef<HTMLElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  // Every opening starts on the scene, scrolled to the top.
  useEffect(() => {
    if (!open) return undefined;
    setView({ kind: 'list', scope: 'scene' });
    panelRef.current?.focus({ preventScroll: true });
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeRef.current();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
    // Tell the player what the sheet is about now (see SheetFocus).
    if (view.kind === 'list') onFocusChange(view.scope === 'scene' ? { kind: 'scene', hoverId: null } : { kind: 'none' });
    else onFocusChange({ kind: 'open', item: view.kind === 'match' ? view.parent : view.item });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const listScope = view.kind === 'list' ? view.scope : view.from;
  const items = listScope === 'scene' ? sceneItems : episodeItems;

  const header =
    view.kind === 'list' ? (
      <header className="tv-panel__header">
        <div className="tv-panel__top">
          <p className="tv-panel__brand">
            <SilvrMark size={12} /> Silvr
          </p>
          <button type="button" className="tv-icon-btn" onClick={onClose} aria-label="Close">
            <XIcon size={16} />
          </button>
        </div>
        <h2 className="tv-panel__title">{view.scope === 'scene' ? 'Shop this scene' : 'Shop this episode'}</h2>
        <p className="tv-panel__context">
          <span className="tv-panel__dot" aria-hidden="true" />
          {contextLabel}
        </p>
      </header>
    ) : (
      <header className="tv-panel__header tv-panel__header--item">
        <div className="tv-panel__top">
          <button
            type="button"
            className="tv-back"
            onClick={() => setView(view.kind === 'match' ? { kind: 'item', item: view.parent, tab: 'similar', from: view.from } : { kind: 'list', scope: view.from })}
          >
            <ChevronLeftIcon /> {view.kind === 'match' ? 'Similar matches' : 'Back'}
          </button>
          <button type="button" className="tv-icon-btn" onClick={onClose} aria-label="Close">
            <XIcon size={16} />
          </button>
        </div>
        {view.kind === 'item' && (
          <div className="tv-tabs" role="tablist">
            {(['details', 'similar'] as const).map((tab) => (
              <button key={tab} type="button" role="tab" aria-selected={view.tab === tab} className={`tv-tab ${view.tab === tab ? 'is-active' : ''}`} onClick={() => setView({ ...view, tab })}>
                {tab === 'details' ? 'Details' : 'Similar matches'}
              </button>
            ))}
          </div>
        )}
      </header>
    );

  let body: React.ReactNode;
  if (view.kind === 'list') {
    body = (
      <>
        {view.scope === 'episode' && (
          <button type="button" className="tv-back tv-back--inline" onClick={() => setView({ kind: 'list', scope: 'scene' })}>
            <ChevronLeftIcon /> Back to this scene
          </button>
        )}
        {items.length ? (
          <>
            <div className="tv-cards">
              {items.map((item) => (
                <ItemCard
                  key={item.id}
                  item={item}
                  onOpen={() => setView({ kind: 'item', item, tab: 'details', from: view.scope })}
                  onSimilar={() => setView({ kind: 'item', item, tab: 'similar', from: view.scope })}
                  onHover={view.scope === 'scene' ? (hoverId) => onFocusChange({ kind: 'scene', hoverId }) : () => undefined}
                  pointed={view.scope === 'scene' && item.id === pointedId}
                />
              ))}
            </div>
          </>
        ) : (
          <p className="tv-empty" role="status">
            No items are published for this moment. Try another frame.
          </p>
        )}
        {view.scope === 'scene' && episodeItems.length > 0 && (
          <button type="button" className="tv-view-all" onClick={() => setView({ kind: 'list', scope: 'episode' })}>
            View all shoppable items in this episode
          </button>
        )}
      </>
    );
  } else if (view.kind === 'match') {
    body = <Details item={view.match} />;
  } else if (view.tab === 'similar') {
    const { item } = view;
    body = (
      <>
        <h3 className="tv-similar__title">Similar matches</h3>
        <p className="tv-similar__subtitle">Items with a similar look &amp; feel to {item.name}</p>
        {item.similar.length ? (
          <ul className="tv-matches">
            {item.similar.map((match) => (
              <MatchRow key={match.id} item={match} onOpen={() => setView({ kind: 'match', match, parent: item, from: view.from })} />
            ))}
          </ul>
        ) : (
          <p className="tv-empty" role="status">
            No similar matches for this item yet.
          </p>
        )}
      </>
    );
  } else {
    body = <Details item={view.item} />;
  }

  // The item count belongs to the lists only; an open item's footer says nothing but "Powered by Silvr"
  // (Paula, 2026-10-29: no "6 similar matches" at the bottom).
  const footerCount = view.kind === 'list' ? `${items.length} item${items.length === 1 ? '' : 's'} in this ${listScope}` : null;

  return (
    <aside ref={panelRef} tabIndex={-1} className={`tv-panel silvr-ui ${open ? 'is-open' : ''}`} aria-hidden={!open} aria-label="Shop this scene" data-silvr-ui="true" onClick={(event) => event.stopPropagation()}>
      {header}
      <div ref={scrollRef} className="tv-panel__scroll" data-tv-scroll="true">
        {body}
      </div>
      <footer className="tv-panel__footer">
        <span>{footerCount}</span>
        <span>
          Powered by <strong>Silvr</strong>
        </span>
      </footer>
    </aside>
  );
};

export default ShopPanel;
